import { useState } from 'react';
import { apiFetch } from '../lib/api';

// Sprint 11 UX.F2 (F1 follow-up) — Bundle mapping approval UI.
//
// Shown immediately after a Claude Design bundle is uploaded
// (ClaudeDesignBundleUpload returns a parsed summary + mapping plan).
// The PM reviews "what would be added" and clicks "Approve & Merge". On
// approval the API merges the new screens into screen_inventory and the
// new components into component_catalogue, creating new versions of each.

interface MappingPlan {
  newScreens:        { id: string; name: string; route?: string }[];
  newComponents:     { id: string; name: string; type?: string }[];
  componentConflicts: { componentName: string; field: string; bundleValue: string; existingValue: string }[];
  matchedScreens:    string[];
  matchedComponents: string[];
}

interface Props {
  projectId:        string;
  bundleArtefactId: string;
  plan:             MappingPlan;
  onMerged?:        (result: { mergedScreenCount: number; mergedComponentCount: number }) => void;
}

export default function BundleMappingApproval({ projectId, bundleArtefactId, plan, onMerged }: Props) {
  const [merging, setMerging]               = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [merged, setMerged]                 = useState<{ mergedScreenCount: number; mergedComponentCount: number } | null>(null);

  // The PM can deselect items they don't want merged. Default = accept all.
  const [acceptedScreenIds, setAcceptedScreenIds] = useState<Set<string>>(
    new Set(plan.newScreens.map(s => s.id)),
  );
  const [acceptedComponentIds, setAcceptedComponentIds] = useState<Set<string>>(
    new Set(plan.newComponents.map(c => c.id)),
  );

  const toggleScreen = (id: string) => {
    setAcceptedScreenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleComponent = (id: string) => {
    setAcceptedComponentIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleApprove = async () => {
    setMerging(true);
    setError(null);
    try {
      const result = await apiFetch<{ mergedScreenCount: number; mergedComponentCount: number }>(
        `/projects/${projectId}/claude-design/${bundleArtefactId}/merge`,
        {
          method: 'POST',
          body: JSON.stringify({
            acceptedScreenIds:    Array.from(acceptedScreenIds),
            acceptedComponentIds: Array.from(acceptedComponentIds),
          }),
        },
      );
      setMerged(result);
      onMerged?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  if (merged) {
    return (
      <div role="status" style={{ padding: 16, borderRadius: 'var(--radius-md)', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--success)', margin: '0 0 4px' }}>
          ✓ Bundle merged
        </p>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: 0 }}>
          {merged.mergedScreenCount} screen{merged.mergedScreenCount === 1 ? '' : 's'} added to the inventory and{' '}
          {merged.mergedComponentCount} component{merged.mergedComponentCount === 1 ? '' : 's'} added to the catalogue.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, borderRadius: 'var(--radius-md)', background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>
        Bundle Analysis
      </h3>

      <ul style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: 0, paddingLeft: 18 }}>
        <li>New screens: <strong>{plan.newScreens.length}</strong></li>
        <li>New components: <strong>{plan.newComponents.length}</strong></li>
        <li>Component conflicts: <strong style={{ color: plan.componentConflicts.length > 0 ? 'var(--warning)' : 'inherit' }}>{plan.componentConflicts.length}</strong></li>
        <li>Already-present screens: {plan.matchedScreens.length}</li>
        <li>Already-present components: {plan.matchedComponents.length}</li>
      </ul>

      {plan.newScreens.length > 0 && (
        <>
          <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, margin: '16px 0 8px', color: 'var(--text)' }}>
            New screens to add
          </h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {plan.newScreens.map(s => (
              <li key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                <input
                  type="checkbox"
                  id={`scr-${s.id}`}
                  checked={acceptedScreenIds.has(s.id)}
                  onChange={() => toggleScreen(s.id)}
                  disabled={merging}
                />
                <label htmlFor={`scr-${s.id}`} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
                  <code style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.id}</code> {s.name}
                  {s.route && <span style={{ color: 'var(--text-secondary)' }}> — {s.route}</span>}
                </label>
              </li>
            ))}
          </ul>
        </>
      )}

      {plan.newComponents.length > 0 && (
        <>
          <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, margin: '16px 0 8px', color: 'var(--text)' }}>
            New components to add
          </h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {plan.newComponents.map(c => (
              <li key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                <input
                  type="checkbox"
                  id={`cmp-${c.id}`}
                  checked={acceptedComponentIds.has(c.id)}
                  onChange={() => toggleComponent(c.id)}
                  disabled={merging}
                />
                <label htmlFor={`cmp-${c.id}`} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
                  <code style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{c.id}</code> {c.name}
                  {c.type && <span style={{ color: 'var(--text-secondary)' }}> ({c.type})</span>}
                </label>
              </li>
            ))}
          </ul>
        </>
      )}

      {plan.componentConflicts.length > 0 && (
        <>
          <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, margin: '16px 0 8px', color: 'var(--warning)' }}>
            Conflicts (review before approving)
          </h4>
          <ul style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0, paddingLeft: 18 }}>
            {plan.componentConflicts.map((c, i) => (
              <li key={i}>
                <strong>{c.componentName}</strong> {c.field}: bundle says <code>{c.bundleValue}</code>, catalogue says <code>{c.existingValue}</code>
              </li>
            ))}
          </ul>
        </>
      )}

      {error && (
        <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', margin: '12px 0 0' }}>
          {error}
        </p>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button
          onClick={() => void handleApprove()}
          disabled={merging || (acceptedScreenIds.size === 0 && acceptedComponentIds.size === 0)}
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 13,
            fontWeight: 600,
            padding: '6px 14px',
            color: 'white',
            background: 'var(--primary)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: merging ? 'progress' : 'pointer',
            opacity: merging || (acceptedScreenIds.size === 0 && acceptedComponentIds.size === 0) ? 0.5 : 1,
          }}
        >
          {merging
            ? 'Merging…'
            : `Approve & Merge (${acceptedScreenIds.size} screen${acceptedScreenIds.size === 1 ? '' : 's'}, ${acceptedComponentIds.size} component${acceptedComponentIds.size === 1 ? '' : 's'})`}
        </button>
      </div>
    </div>
  );
}
